import { useState } from "react";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import AssetAutocomplete from "components/holdings/AssetAutocomplete";
import { HeldPeriod } from "features/trading/heldPeriods";
import { BucketActions } from "./useBucketActions";

interface BucketAddAssetRowProps {
  accountId: number;
  actions: Pick<BucketActions, "addAsset" | "isAddingAsset">;
  maxWidth?: number;
  // The report's periods, when this row is opened from the report settings.
  // Absent on the client page's Buckets tab, which has no reporting period.
  heldPeriods?: HeldPeriod[];
}

/**
 * Autocomplete + Add button for Buckets 1/2. The picker is restricted to
 * the securities this bucket's account held — over `heldPeriods` when they
 * are given, otherwise what it holds now. Shared between the Buckets tab
 * and the edit modal.
 */
export const BucketAddAssetRow = ({
  accountId,
  actions,
  maxWidth,
  heldPeriods,
}: BucketAddAssetRowProps) => {
  const [assetToAdd, setAssetToAdd] = useState<number | "">("");

  const handleAdd = async () => {
    if (assetToAdd === "") return;
    const ok = await actions.addAsset(assetToAdd);
    if (ok) setAssetToAdd("");
  };

  return (
    <Box display="flex" gap={1} alignItems="center">
      <Box flexGrow={1} maxWidth={maxWidth}>
        <AssetAutocomplete
          value={assetToAdd}
          onChange={setAssetToAdd}
          heldByAccountId={accountId}
          heldPeriods={heldPeriods}
          label="Add asset"
          // Kept short: the modal's field clips a longer line mid-word.
          placeholder={
            heldPeriods?.length
              ? "Held in this account during the period"
              : "Currently held in this account"
          }
        />
      </Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        disabled={assetToAdd === "" || actions.isAddingAsset}
        onClick={handleAdd}
      >
        Add
      </Button>
    </Box>
  );
};
